import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { ProductImage, ProductImageCreateResponse, ProductImageReorderItem } from '../../../types';

interface UseProductImagesParams {
  chainId: number | '';
  upc: string;
}

export function useProductImages({ chainId, upc }: UseProductImagesParams) {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);

  // All images sorted by position
  const allImages = useMemo(() => [...images].sort((a, b) => a.position - b.position), [images]);

  // Filter to current chain and sort by position
  const chainImages = useMemo(() => {
    if (!chainId) return [];
    return allImages.filter((img) => img.chainId === Number(chainId));
  }, [allImages, chainId]);

  const primaryImage = useMemo(() => chainImages[0] ?? null, [chainImages]);
  const alternativeImages = useMemo(() => chainImages.slice(1), [chainImages]);

  const fetchImages = useCallback(async () => {
    if (!token || !chainId || !upc) {
      setImages([]);
      return;
    }

    setLoading(true);
    try {
      const result = await ApiService.getProductImages(token, Number(chainId), upc);
      setImages(result);
    } catch (err) {
      console.error('Error fetching product images:', err);
    } finally {
      setLoading(false);
    }
  }, [token, chainId, upc]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const uploadImage = useCallback(
    async (
      imageFile: Blob,
      position: number = 0,
      source?: string,
      origin?: string
    ): Promise<ProductImageCreateResponse | null> => {
      if (!token || !chainId || !upc) return null;

      try {
        const result = await ApiService.uploadProductImage(
          token,
          Number(chainId),
          upc,
          imageFile,
          position,
          source,
          origin
        );
        return result;
      } catch (err) {
        showErrorToast('Failed to upload product image');
        console.error('Error uploading product image:', err);
        return null;
      }
    },
    [token, chainId, upc, showErrorToast]
  );

  const deleteImage = useCallback(
    async (productImageId: number): Promise<boolean> => {
      if (!token) return false;

      try {
        await ApiService.deleteProductImage(token, productImageId);
        return true;
      } catch (err) {
        showErrorToast('Failed to delete product image');
        console.error('Error deleting product image:', err);
        return false;
      }
    },
    [token, showErrorToast]
  );

  const reorderImages = useCallback(
    async (positions: ProductImageReorderItem[]): Promise<boolean> => {
      if (!token) return false;

      try {
        await ApiService.reorderProductImages(token, positions);
        showSuccessToast('Image order updated');
        return true;
      } catch (err) {
        showErrorToast('Failed to reorder images');
        console.error('Error reordering product images:', err);
        return false;
      }
    },
    [token, showErrorToast, showSuccessToast]
  );

  return {
    images: chainImages,
    allImages,
    primaryImage,
    alternativeImages,
    loading,
    refetch: fetchImages,
    uploadImage,
    deleteImage,
    reorderImages,
  };
}
